import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { AuthCallbackPage, LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { LicensesPage } from './pages/Licenses';
import { LicenseDetailPage } from './pages/LicenseDetail';
import { ProductDetailPage, ProductsPage } from './pages/Products';
import { CustomerDetailPage, CustomersPage } from './pages/Customers';
import { MachinesPage } from './pages/Machines';
import { AuditPage } from './pages/Audit';
import { OfflinePage, PublicOfflinePortal } from './pages/Offline';
import { WebhooksPage } from './pages/Webhooks';
import { ManualPage } from './pages/Manual';
import { I18nProvider, useT } from './i18n';

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/** Título de la pestaña en el idioma activo. */
function DocumentTitle() {
  const t = useT();
  useEffect(() => {
    document.title = t('meta.title');
  }, [t]);
  return null;
}

export function App() {
  return (
    <I18nProvider>
      <DocumentTitle />
      <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/portal/activacion-offline" element={<PublicOfflinePortal />} />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="licencias" element={<LicensesPage />} />
            <Route path="licencias/:id" element={<LicenseDetailPage />} />
            <Route path="productos" element={<ProductsPage />} />
            <Route path="productos/:id" element={<ProductDetailPage />} />
            <Route path="clientes" element={<CustomersPage />} />
            <Route path="clientes/:id" element={<CustomerDetailPage />} />
            <Route path="maquinas" element={<MachinesPage />} />
            <Route path="auditoria" element={<AuditPage />} />
            <Route path="activacion-offline" element={<OfflinePage />} />
            <Route path="webhooks" element={<WebhooksPage />} />
            <Route path="manual" element={<ManualPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
